import { useState, useCallback } from "react";
import { Modal, Button, Text, Title } from "rizzui";
import { BiCheck, BiX } from "react-icons/bi";
import UnitsRepository from "@/utilities/repositories/Units";
import toast from "react-hot-toast";
import cn from "@/utils/class-names";

interface BulkMarkAsSoldModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess: () => void;
}

interface ResultItem {
  name: string;
  success: boolean;
  error?: string;
}

export default function BulkMarkAsSoldModal({
  isOpen,
  onClose,
  projectId,
  onSuccess,
}: BulkMarkAsSoldModalProps) {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<ResultItem[] | null>(null);

  const unitNames = inputValue
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const handleSubmit = useCallback(async () => {
    if (unitNames.length === 0) {
      toast.error("Please enter at least one unit name");
      return;
    }

    setIsLoading(true);
    try {
      const response = await UnitsRepository.bulkMarkAsSold(
        projectId,
        unitNames,
      );
      const { updated = [], errors = [] } = (response as any)?.data ?? {};

      const resultItems: ResultItem[] = [
        ...updated.map((name: string) => ({ name, success: true })),
        ...errors.map((e: { unitName: string; error: string }) => ({
          name: e.unitName,
          success: false,
          error: e.error,
        })),
      ];

      setResults(resultItems);

      if (updated.length > 0) {
        toast.success(`${updated.length} unit(s) marked as sold`);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to mark units as sold");
    } finally {
      setIsLoading(false);
    }
  }, [unitNames, projectId]);

  const handleClose = useCallback(() => {
    if (results) {
      onSuccess();
    }
    setInputValue("");
    setResults(null);
    onClose();
  }, [results, onClose, onSuccess]);

  const successCount = results?.filter((r) => r.success).length ?? 0;
  const errorCount = results?.filter((r) => !r.success).length ?? 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="p-6">
        <Title as="h3" className="text-lg font-semibold mb-4">
          Bulk Mark as Sold
        </Title>

        {!results ? (
          <>
            <Text className="mb-2 text-sm text-gray-600">
              Enter unit names (one per line):
            </Text>
            <textarea
              className="w-full h-48 p-3 border border-gray-300 rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={"03-25\n#03-45\n02-15"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isLoading}
            />
            <Text className="mt-2 text-xs text-gray-500">
              Format: 03-25 or #03-45 are both accepted
            </Text>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={isLoading}
                disabled={unitNames.length === 0}
              >
                Mark as Sold
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left w-10"></th>
                    <th className="px-4 py-2 text-left">Unit</th>
                    <th className="px-4 py-2 text-left">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((item, index) => (
                    <tr
                      key={index}
                      className={cn(
                        "border-t",
                        item.success
                          ? "bg-green-50"
                          : "bg-red-50",
                      )}
                    >
                      <td className="px-4 py-2 text-center">
                        {item.success ? (
                          <BiCheck className="text-green-600" />
                        ) : (
                          <BiX className="text-red-600" />
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono">{item.name}</td>
                      <td
                        className={cn(
                          "px-4 py-2 text-xs",
                          item.success
                            ? "text-green-700"
                            : "text-red-600",
                        )}
                      >
                        {item.success
                          ? "Marked as sold"
                          : item.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <Text className="text-sm text-gray-600">
                Summary:{" "}
                <span className="text-green-600">
                  {successCount} updated
                </span>
                {errorCount > 0 && (
                  <span className="text-red-600">
                    , {errorCount} errors
                  </span>
                )}
              </Text>
              <Button onClick={handleClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
